import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EmailIcon from "@mui/icons-material/Email";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FiltroMultiple from "../../Postventa/Dashboard/Modales/FiltroMultiple";
import ModalOptions from "../../Auditoria/Helper/modalOptions";
import { ModalEnviarCorreoCotizacion } from "./ModalEnviarCorreoCotizacion";
import { FormatMoney } from "../../../Helpers/FormatNumber";

const PLANTILLAS_PDF = [
  { value: "dron", label: "Plantilla Dron" },
  { value: "riego", label: "Plantilla Riego" },
  { value: "servicio", label: "Plantilla Servicio" },
];

// Cuando la cotización ya trae línea capturada (CRM.generales "Linea_Cotizacion"), la plantilla
// se determina sola a partir de ese texto — no hace falta preguntarle al usuario cuál usar.
const PLANTILLA_POR_LINEA = {
  servicio: "servicio",
  drones: "dron",
  riego: "riego",
};

const plantillaPorLinea = (lineaCotizacion) =>
  PLANTILLA_POR_LINEA[(lineaCotizacion || "").trim().toLowerCase()] || null;

const CAMPOS_FILTRO = ["razon_social", "propietario", "asesor", "sucursal", "linea_cotizacion", "origen", "tipo_pago"];
const ESTADO_VACIO_FILTROS = CAMPOS_FILTRO.reduce((acc, k) => ({ ...acc, [k]: [] }), {});
const ESTADO_VACIO_MOSTRAR = CAMPOS_FILTRO.reduce((acc, k) => ({ ...acc, [k]: { open: false } }), {});

const formatFecha = (valor) => {
  if (!valor) return "";
  const fecha = new Date(valor);
  if (Number.isNaN(fecha.getTime())) return valor;
  return fecha.toLocaleDateString("es-MX");
};

// Botón PDF con menú de plantillas — posiciona el menú con position: fixed calculado desde
// getBoundingClientRect (mismo patrón que FiltroMultiple) para que no quede recortado por el
// overflow del contenedor con scroll horizontal de la tabla ni se desborde fuera del viewport.
// Se renderiza con un portal a document.body: la celda de acciones es sticky (stacking context
// propio), así que sin portal el menú de una fila quedaba tapado por las celdas sticky de las
// filas siguientes pese a su z-index alto.
const BotonPdfPlantilla = ({ folio, plantillaFija, onGeneraPDF }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);

  const toggle = () => {
    if (plantillaFija) {
      onGeneraPDF && onGeneraPDF(folio, plantillaFija);
      return;
    }
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const anchoMenu = 170;
      let left = rect.left;
      let top = rect.bottom + 4;

      if (left + anchoMenu > window.innerWidth) {
        left = window.innerWidth - anchoMenu - 10;
      }
      if (left < 10) left = 10;

      setPos({ top, left });
    }
    setOpen((prev) => !prev);
  };

  return (
    <div className="crmcs-btn-pdf-wrap">
      <button
        ref={buttonRef}
        type="button"
        className="crmcs-btn-pdf"
        onClick={toggle}
        title="Descargar PDF"
      >
        <PictureAsPdfIcon />
      </button>
      {open && createPortal(
        <div className="crmcs-btn-pdf-menu-fixed" style={{ top: pos.top, left: pos.left }}>
          <ModalOptions
            onClose={() => setOpen(false)}
            options={PLANTILLAS_PDF.map((p) => ({
              label: p.label,
              onClick: () => onGeneraPDF && onGeneraPDF(folio, p.value),
            }))}
          />
        </div>,
        document.body
      )}
    </div>
  );
};

// Botón "Enviar por correo" — mismo patrón que BotonPdfPlantilla (menú de plantillas con
// portal); al elegir la plantilla abre el modal de envío en vez de descargar directo.
const BotonEnviarCorreoPlantilla = ({ folio, idcliente, plantillaFija, onAbrirModalCorreo }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);

  const toggle = () => {
    if (plantillaFija) {
      onAbrirModalCorreo(folio, plantillaFija, idcliente);
      return;
    }
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const anchoMenu = 170;
      let left = rect.left;
      let top = rect.bottom + 4;

      if (left + anchoMenu > window.innerWidth) {
        left = window.innerWidth - anchoMenu - 10;
      }
      if (left < 10) left = 10;

      setPos({ top, left });
    }
    setOpen((prev) => !prev);
  };

  return (
    <div className="crmcs-btn-pdf-wrap">
      <button
        ref={buttonRef}
        type="button"
        className="crmcs-btn-pdf"
        onClick={toggle}
        title="Enviar por correo"
      >
        <EmailIcon />
      </button>
      {open && createPortal(
        <div className="crmcs-btn-pdf-menu-fixed" style={{ top: pos.top, left: pos.left }}>
          <ModalOptions
            onClose={() => setOpen(false)}
            options={PLANTILLAS_PDF.map((p) => ({
              label: p.label,
              onClick: () => onAbrirModalCorreo(folio, p.value, idcliente),
            }))}
          />
        </div>,
        document.body
      )}
    </div>
  );
};

// Tabla de Cotizaciones de Servicio — thead/celdas con la misma estructura y comportamiento que
// CotizacionesTablaScreen (Ventas/Cotizaciones): sin colgroup, table-layout: auto, cada columna
// se dimensiona según su contenido y empuja a las demás con scroll horizontal. Se añaden, sobre
// ese mismo patrón, esquinas redondeadas (wrapper propio) y una fila de total dentro del mismo
// tbody fijada abajo con position: sticky (en vez de una segunda tabla con colgroup propio).
export const CotizacionesServicioTabla = ({ listado: listadoProp, onGeneraPDF, onEditar, onEliminar }) => {
  const listado = Array.isArray(listadoProp) ? listadoProp : [];
  const [options, setOptions] = useState(ESTADO_VACIO_FILTROS);
  const [filtros, setFiltros] = useState(ESTADO_VACIO_FILTROS);
  const [filtrosTemp, setFiltrosTemp] = useState(ESTADO_VACIO_FILTROS);
  const [mostrarFiltro, setMostrarFiltro] = useState(ESTADO_VACIO_MOSTRAR);
  const [prioridadFiltros, setPrioridadFiltros] = useState([]);
  const [modalCorreo, setModalCorreo] = useState(null);

  const onAbrirModalCorreo = (folio, plantilla, idcliente) => {
    setModalCorreo({ folio, plantilla, idcliente });
  };

  useEffect(() => {
    const nuevasOptions = {};
    CAMPOS_FILTRO.forEach((campo) => {
      nuevasOptions[campo] = [...new Set(listado.map((c) => c[campo]).filter(Boolean))].map((v) => ({ value: v, label: v }));
    });
    setOptions(nuevasOptions);
  }, [listado]);

  const actualizarPrioridad = (filtro) => {
    setPrioridadFiltros((prev) => {
      const index = prev.indexOf(filtro);
      const nuevasPrioridades = index === -1 ? [...prev, filtro] : prev.slice(0, index + 1);

      setFiltrosTemp((prevTemp) => {
        const limpio = { ...prevTemp };
        Object.keys(limpio).forEach((key) => {
          if (!nuevasPrioridades.includes(key)) limpio[key] = [];
        });
        return limpio;
      });

      setFiltros((prevFiltros) => {
        const limpio = { ...prevFiltros };
        Object.keys(limpio).forEach((key) => {
          if (!nuevasPrioridades.includes(key)) limpio[key] = [];
        });
        return limpio;
      });

      return nuevasPrioridades;
    });
  };

  const listadoFiltrado = useMemo(() => {
    let data = [...listado];

    prioridadFiltros.forEach((filtro) => {
      if (filtros[filtro]?.length > 0) {
        data = data.filter((c) => filtros[filtro].includes(c[filtro]));
      }
    });

    return data;
  }, [listado, filtros, prioridadFiltros]);

  const totalMonto = listadoFiltrado.reduce((acc, c) => acc + (c.total || 0), 0);

  const filtroProps = (field, label) => ({
    label,
    field,
    options: options[field],
    filtros,
    setFiltros,
    filtrosTemp,
    setFiltrosTemp,
    mostrarFiltro,
    setMostrarFiltro,
    actualizarPrioridad,
    setPrioridadFiltros,
  });

  return (
    <div className="crmcs-cot-tabla-wrap">
      <div className="crmcs-cot-tabla-scroll scroll">
        <table cellPadding={0} cellSpacing={0}>
          <thead>
            <tr>
              <th className="crmcs-th-sticky crmcs-th-sticky-acciones"></th>
              <th className="crmcs-th-sticky crmcs-th-sticky-cliente">
                <div className="borde-izq">
                  <FiltroMultiple {...filtroProps("razon_social", "Cliente")} />
                </div>
              </th>
              <th className="crmcs-th-sticky crmcs-th-sticky-propietario">
                <div className="borde-izq">
                  <FiltroMultiple {...filtroProps("propietario", "Propietario")} />
                </div>
              </th>
              <th>
                <div className="borde-izq">
                  <FiltroMultiple {...filtroProps("asesor", "Asesor")} />
                </div>
              </th>
              <th>
                <div className="borde-izq">
                  <FiltroMultiple {...filtroProps("sucursal", "Sucursal")} />
                </div>
              </th>
              <th>
                <div className="borde-izq">Asunto</div>
              </th>
              <th>
                <div className="borde-izq">Vigencia</div>
              </th>
              <th>
                <div className="borde-izq">
                  <FiltroMultiple {...filtroProps("linea_cotizacion", "Línea")} />
                </div>
              </th>
              <th>
                <div className="borde-izq">
                  <FiltroMultiple {...filtroProps("origen", "Origen")} />
                </div>
              </th>
              <th>
                <div className="borde-izq">
                  <FiltroMultiple {...filtroProps("tipo_pago", "Tipo de pago")} />
                </div>
              </th>
              <th>
                <div className="borde-izq">Importe</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {listadoFiltrado.map((c) => {
              const plantillaFija = plantillaPorLinea(c.linea_cotizacion);
              return (
                <tr key={c.folio}>
                  <td className="crmcs-td-sticky crmcs-td-sticky-acciones">
                    <div className="crmcs-acciones-tabla">
                      <BotonPdfPlantilla folio={c.folio} plantillaFija={plantillaFija} onGeneraPDF={onGeneraPDF} />
                      <BotonEnviarCorreoPlantilla folio={c.folio} idcliente={c.idcliente} plantillaFija={plantillaFija} onAbrirModalCorreo={onAbrirModalCorreo} />
                      {onEditar && (
                        <button
                          type="button"
                          className="crmcs-btn-icono"
                          onClick={() => onEditar(c.folio)}
                          title="Editar cotización"
                        >
                          <EditIcon />
                        </button>
                      )}
                      {onEliminar && (
                        <button
                          type="button"
                          className="crmcs-btn-icono danger"
                          onClick={() => onEliminar(c.folio)}
                          title="Eliminar cotización"
                        >
                          <DeleteIcon />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="crmcs-td-sticky crmcs-td-sticky-cliente">{c.razon_social}</td>
                  <td className="crmcs-td-sticky crmcs-td-sticky-propietario">{c.propietario}</td>
                  <td>{c.asesor}</td>
                  <td>{c.sucursal}</td>
                  <td>{c.asunto}</td>
                  <td>{formatFecha(c.vigencia)}</td>
                  <td>{c.linea_cotizacion}</td>
                  <td>{c.origen}</td>
                  <td>{c.tipo_pago}</td>
                  <td>{FormatMoney(c.total)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="crmcs-cot-tabla-total">
        <span>Total ({listadoFiltrado.length} cotizaciones)</span>
        <span>{FormatMoney(totalMonto)}</span>
      </div>
      <ModalEnviarCorreoCotizacion
        open={!!modalCorreo}
        folio={modalCorreo?.folio}
        plantilla={modalCorreo?.plantilla}
        idcliente={modalCorreo?.idcliente}
        onClose={() => setModalCorreo(null)}
      />
    </div>
  );
};
